/**
 * Lead pipeline stage constants (Round 18.2). The Kanban board's five columns
 * map to these stages. `LeadStage` + the `Lead`/`LeadSummary` types live in
 * api.ts (the backend contract); this module owns the presentation metadata.
 */
import type { LeadDetail, LeadStage, LeadSummary } from "./api";

export type { LeadStage } from "./api";

// The Kanban board operates on lead summaries.
export type Lead = LeadSummary;

// Narrow a full LeadDetail (e.g. returned by updateLeadStage, which — like
// getLeadDetail — returns the lead row verbatim) down to the LeadSummary
// shape the Kanban board operates on. Mainly needed because LeadDetail's
// quoteResult is the real, full @dockpro/core QuoteResult, not LeadSummary's
// simplified { lowEstimate, highEstimate } (built by the list handler) —
// the two are different response shapes, not one extending the other.
export function toLeadSummary(detail: LeadDetail): Lead {
  return {
    leadId: detail.leadId,
    idempotencyKey: detail.idempotencyKey,
    submissionTimestamp: detail.submissionTimestamp,
    homeownerName: detail.homeownerName,
    homeownerEmail: detail.homeownerEmail,
    homeownerPhone: detail.homeownerPhone,
    submissionStatus: detail.submissionStatus,
    stage: detail.stage,
    city: detail.city,
    zip: detail.zip,
    receivedAt: detail.receivedAt,
    ...(detail.quoteResult
      ? { quoteResult: { lowEstimate: detail.quoteResult.range[0], highEstimate: detail.quoteResult.range[1] } }
      : {}),
  };
}

export const LEAD_STAGES: readonly LeadStage[] = [
  "new", "contacted", "quoted", "won", "lost",
] as const;

export const STAGE_LABELS: Record<LeadStage, string> = {
  new: "New",
  contacted: "Contacted",
  quoted: "Quoted",
  won: "Won",
  lost: "Lost",
};

export const STAGE_COLORS: Record<LeadStage, string> = {
  new: "#6c757d",        // gray
  contacted: "#0d6efd",  // blue
  // AUD-018 (axe: serious) — the original #fd7e14 only reached 2.57:1
  // contrast against the white badge text (.stage-badge in global.css),
  // failing WCAG AA's 4.5:1. The audit's OWN suggested replacement
  // (#c96410) was verified (see contrast.test.ts) to still only reach
  // ~3.95:1 — also a fail — so this is a different, darker burnt-orange
  // that actually clears 4.5:1 (~4.66:1), verified the same way rather
  // than trusted on sight.
  quoted: "#c05400",     // orange (darkened for contrast — see AUD-018)
  won: "#198754",        // green
  lost: "#dc3545",       // red
};

// Legacy leads created before Round 18.2 have no `stage` — treat as "new".
export function stageOf(lead: Pick<Lead, "stage">): LeadStage {
  return lead.stage ?? "new";
}
